<template>
    <kt-modal
        :visible="true"
        title="Add Items from Job"
        @dismiss="onDismiss"
        size="modal-lg"
    >
        <template #body>
            <div v-if="loading" class="text-center py-5">
                <i class="fa fa-spinner fa-spin fa-2x"></i>
                <p class="mt-2">Loading job items...</p>
            </div>

            <div v-else-if="!props.requestId" class="alert alert-warning">
                <i class="fa fa-exclamation-triangle"></i>
                This work order is not linked to a job. Please link a job first to import items.
            </div>

            <div v-else>
                <div v-if="allItems.length === 0" class="alert alert-info">
                    <i class="fa fa-info-circle"></i>
                    No tasks, parts, expenses, or mileage found for this job.
                </div>

                <div v-else>
                    <p class="text-muted mb-3">
                        Select the items you want to add to this work order:
                    </p>

                    <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                        <table class="table table-sm table-bordered table-hover">
                            <thead class="table-light sticky-top">
                                <tr>
                                    <th style="width: 50px;" class="text-center">
                                        <input 
                                            type="checkbox" 
                                            class="form-check-input"
                                            :checked="allSelected"
                                            @change="toggleSelectAll"
                                        />
                                    </th>
                                    <th style="width: 100px;">Type</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in allItems" :key="item.id" :class="{'text-muted': item.isAlreadyInvoiced}">
                                    <td class="text-center">
                                        <input 
                                            type="checkbox" 
                                            class="form-check-input"
                                            v-model="item.selected"
                                            :disabled="item.isAlreadyInvoiced"
                                        />
                                    </td>
                                    <td>
                                        <span v-if="item.type == 'Task'" class="badge" :class="getTypeBadgeClass(item.type)">
                                            Work
                                        </span>
                                        <span v-else class="badge" :class="getTypeBadgeClass(item.type)">
                                            {{ item.type }}
                                        </span>
                                    </td>
                                    <td>
                                        <a 
                                            href="javascript:void(0);" 
                                            @click="openEditModal(item)" 
                                            class="text-decoration-none"
                                            :class="{'text-decoration-line-through': item.isAlreadyInvoiced}"
                                        >
                                            {{ item.description }}
                                        </a>
                                        <span v-if="item.isAlreadyInvoiced" class="badge bg-secondary ms-2">Already Invoiced</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="mt-3 text-muted small">
                        <strong>{{ selectedCount }}</strong> item(s) selected
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="row w-100">
                <div class="col-4">
                    <button @click="onDismiss" class="btn btn-sm btn-outline-secondary form-control">
                        Cancel
                    </button>
                </div>
                <div class="col-4">
                    <button 
                        @click="combineSelectedItems" 
                        class="btn btn-sm btn-outline-success form-control"
                        :disabled="selectedCount === 0 || adding"
                        title="Combine all selected items into one line item"
                    >
                        <i class="fa fa-spinner fa-spin" v-if="adding"></i>
                        <i class="fa fa-compress-arrows-alt" v-else></i>
                        Combine
                    </button>
                </div>
                <div class="col-4">
                    <button 
                        @click="addSelectedItems" 
                        class="btn btn-sm btn-outline-primary form-control"
                        :disabled="selectedCount === 0 || adding"
                    >
                        <i class="fa fa-spinner fa-spin" v-if="adding"></i>
                        <i class="fa fa-plus" v-else></i>
                        Add ({{ selectedCount }})
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import Api from '@/services/ApiService';
import Alert from '@/services/Alert';
import Emitter from '@/services/Emitter';
import { WorkOrderItemModel } from '@/models/WorkOrderItemModel';
import { TaskModel } from "@/models/TaskModel";
import { PartModel } from "@/models/PartModel";
import { MileageModel } from "@/models/MileageModel";
import { ExpenseModel } from "@/models/ExpenseModel";


const props = defineProps({
    requestId: {
        type: Number,
        required: true
    },
    clientId: {
        type: Number,
        default: null
    },
    workOrderId: {
        type: Number,
        required: true
    }
});

const emit = defineEmits(['close']);

interface JobItem {
    id: string;
    type: 'Task' | 'Part' | 'Expense' | 'Mileage';
    description: string;
    selected: boolean;
    sourceData: any;
    workOrderId?: number | null;
    isAlreadyInvoiced: boolean;
}

const loading = ref(true);
const adding = ref(false);
const allItems = ref<JobItem[]>([]);

const selectedCount = computed(() => {
    return allItems.value.filter(item => item.selected).length;
});

const allSelected = computed(() => {
    return allItems.value.length > 0 && allItems.value.every(item => item.selected);
});

const toggleSelectAll = () => {
    const newValue = !allSelected.value;
    allItems.value.forEach(item => {
        item.selected = newValue;
    });
};

const getTypeBadgeClass = (type: string): string => {
    switch (type) {
        case 'Task': return 'bg-primary';
        case 'Part': return 'bg-success';
        case 'Expense': return 'bg-warning';
        case 'Mileage': return 'bg-info';
        default: return 'bg-secondary';
    }
};

const loadJobItems = async () => {
    if (!props.requestId) {
        loading.value = false;
        return;
    }

    try {
        loading.value = true;
        const items: JobItem[] = [];

        // Load Tasks (Time entries with Type='task')
        try {
            const tasksResponse = await Api.loadTimes({ RequestId: [props.requestId], Type: 'task' });
            const tasks:Array<TaskModel> = tasksResponse.Data || [];
            tasks.forEach((task: TaskModel) => {
                const descParts = [];

                descParts.push(task.WorkCompleteDate || task.CompleteByDate || task.CreateDate);
                descParts.push(task.InvoiceText || task.ActualWork || "No Detail");
                descParts.push(task.InvoiceAmount || "No Invoice Amount");

                const isInvoiced = !!(task as any).WorkOrderId;
                items.push({
                    id: `task-${task.Id}`,
                    type: 'Task',
                    description: descParts.length > 0 ? descParts.join(' - ') : 'Task',
                    selected: false,
                    sourceData: task,
                    workOrderId: (task as any).WorkOrderId || null,
                    isAlreadyInvoiced: isInvoiced
                });
            });
        } catch (err) {
            console.error('Failed to load tasks:', err);
        }

        // Load Parts
        try {
            const partsResponse = await Api.loadParts({ RequestId: [props.requestId] });
            const partsData = partsResponse.Data || [];
            partsData.forEach((part: PartModel) => {
                const descParts = [];


                descParts.push(part.WorkCompleteDate || part.NeededBy || part.CreateDate || "No Date");
                descParts.push(part.PartDescription || "No  Desc");
                descParts.push(part.PartNumber || "No Number");
                descParts.push("x" + (part.Quantity || 0));
                descParts.push("$" + (part.MSRP || part.PartsCost || "No Cost"));

                const isInvoiced = !!(part as any).WorkOrderId;
                items.push({
                    id: `part-${part.Id}`,
                    type: 'Part',
                    description: descParts.length > 0 ? descParts.join(' - ') : 'Part',
                    selected: false,
                    sourceData: part,
                    workOrderId: (part as any).WorkOrderId || null,
                    isAlreadyInvoiced: isInvoiced
                });
            });
        } catch (err) {
            console.error('Failed to load parts:', err);
        }

        // Load Expenses
        try {
            const expensesResponse = await Api.loadExpenses({ RequestId: [props.requestId] });
            const expensesData = expensesResponse.Data || [];
            expensesData.forEach((expense: ExpenseModel) => {
                const descParts = [];
                
                
                descParts.push(expense.ExpenseDate || expense.CreateDate || "No Date");
                descParts.push(expense.ExpenseNotes || "No Description");
                descParts.push("$" + (expense.ExpenseAmount || " No Amount"));

                const isInvoiced = !!(expense as any).WorkOrderId;
                items.push({
                    id: `expense-${expense.Id}`,
                    type: 'Expense',
                    description: descParts.length > 0 ? descParts.join(' - ') : 'Expense',
                    selected: false,
                    sourceData: expense,
                    workOrderId: (expense as any).WorkOrderId || null,
                    isAlreadyInvoiced: isInvoiced
                });
            });
        } catch (err) {
            console.error('Failed to load expenses:', err);
        }

        // Load Mileage
        try {
            const mileageResponse = await Api.loadMileages({ RequestId: [props.requestId] });
            const mileageData = mileageResponse.Data || [];
            mileageData.forEach((mile: MileageModel) => {
                const descParts = [];


                descParts.push(mile.DateAndTime || "No Date");
                descParts.push(mile.Notes || "No Notes");
                descParts.push(mile.OdometerEnd);

                const isInvoiced = !!(mile as any).WorkOrderId;
                items.push({
                    id: `mileage-${mile.Id}`,
                    type: 'Mileage',
                    description: descParts.length > 0 ? descParts.join(' - ') : 'Mileage',
                    selected: false,
                    sourceData: mile,
                    workOrderId: (mile as any).WorkOrderId || null,
                    isAlreadyInvoiced: isInvoiced
                });
            });
        } catch (err) {
            console.error('Failed to load mileage:', err);
        }

        allItems.value = items;
    } catch (error) {
        console.error('Failed to load job items:', error);
        Alert.notify({ text: 'Failed to load job items', type: 'error' });
    } finally {
        loading.value = false;
    }
};

const combineSelectedItems = async () => {
    const selected = allItems.value.filter(item => item.selected);
    
    if (selected.length === 0) {
        return;
    }

    try {
        adding.value = true;

        // Create a single combined work order item
        const combinedItem = new WorkOrderItemModel();
        combinedItem.WorkOrderId = props.workOrderId;
        
        // Concatenate all descriptions
        const descriptions = selected.map(item => item.description);
        combinedItem.Description = descriptions.join('\n');
        
        // Sum all prices
        let totalPrice = 0;
        selected.forEach(item => {
            switch (item.type) {
                case 'Task':
                    totalPrice += item.sourceData.InvoiceAmount || 0;
                    break;
                case 'Part':
                    const partCost = (item.sourceData.MSRP || item.sourceData.PartsCost) || 0;
                    const partQty = item.sourceData.Quantity || 1;
                    totalPrice += partCost * partQty;
                    break;
                case 'Expense':
                    totalPrice += item.sourceData.ExpenseAmount || 0;
                    break;
                case 'Mileage':
                    totalPrice += ((item.sourceData.OdometerEnd || 0) - (item.sourceData.OdometerStart || 0)) * 5;
                    break;
            }
        });

        // Set combined item properties
        combinedItem.ItemType = 'Service';
        combinedItem.Quantity = 1;
        combinedItem.UnitCost = totalPrice;
        combinedItem.Taxable = true;
        combinedItem.TaxRate = 0;
        combinedItem.DiscountAmount = 0;
        combinedItem.LineTotal = totalPrice;
        
        // Use current date
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        combinedItem.LineDate = `${year}-${month}-${day}`;

        // Save the combined line item
        await Api.saveWorkOrderItem(combinedItem);

        // Tag source entities with the WorkOrder ID
        await tagSourceEntities(selected);

        Alert.notify({ 
            text: `Successfully combined ${selected.length} item(s) into one line item`, 
            type: 'success' 
        });

        emit('close');
    } catch (error) {
        console.error('Failed to combine items:', error);
        Alert.notify({ text: 'Failed to combine items', type: 'error' });
    } finally {
        adding.value = false;
    }
};

const addSelectedItems = async () => {
    const selected = allItems.value.filter(item => item.selected);
    
    if (selected.length === 0) {
        return;
    }

    try {
        adding.value = true;

        // Create work order items from selected items
        for (const item of selected) {
            const lineItem = new WorkOrderItemModel();
            lineItem.WorkOrderId = props.workOrderId;
            lineItem.Description = item.description;
            lineItem.Quantity = 1;
            
            console.debug("item", item);

            // Set item type, pricing, and source entity reference based on source type
            switch (item.type) {
                case 'Task':
                    lineItem.ItemType = 'Labor';
                    lineItem.UnitCost = item.sourceData.InvoiceAmount || item.sourceData.ShouldBill || 0;
                    lineItem.TaskId = item.sourceData.Id;
                    break;
                case 'Part':
                    lineItem.ItemType = 'Part';
                    lineItem.UnitCost = (item.sourceData.MSRP || item.sourceData.PartsCost) ||  0;
                    lineItem.Quantity = (item.sourceData.Quantity || 1);
                    lineItem.PartId = item.sourceData.Id;
                    break;
                case 'Expense':
                    lineItem.ItemType = 'Other';
                    lineItem.UnitCost = item.sourceData.ExpenseAmount || 0;
                    lineItem.ExpenseId = item.sourceData.Id;
                    break;
                case 'Mileage':
                    lineItem.ItemType = 'Service';
                    // need to implement a rate
                    lineItem.UnitCost = ((item.sourceData.OdometerEnd || 0) - (item.sourceData.OdometerStart || 0) * 5);
                    lineItem.MileageId = item.sourceData.Id;
                    break;
            }

            lineItem.Taxable = true;
            lineItem.TaxRate = 0;
            lineItem.DiscountAmount = 0;
            lineItem.LineTotal = lineItem.UnitCost;

            // Save the line item
            await Api.saveWorkOrderItem(lineItem);
        }

        // Tag source entities with the WorkOrder ID
        await tagSourceEntities(selected);

        Alert.notify({ 
            text: `Successfully added ${selected.length} item(s) to invoice`, 
            type: 'success' 
        });

        emit('close');
    } catch (error) {
        console.error('Failed to add items:', error);
        Alert.notify({ text: 'Failed to add some items', type: 'error' });
    } finally {
        adding.value = false;
    }
};

const openEditModal = (item: JobItem) => {
    // Extract the actual ID from the prefixed id (e.g., "task-123" -> 123)
    const actualId = item.sourceData.Id;
    
    // Emit redirect based on item type
    switch (item.type) {
        case 'Task':
            // Use WorkEdit modal with proper payload structure
            Emitter.emit('open:work', {
                id: actualId,
                requestId: props.requestId,
                clientId: props.clientId,
                techId: item.sourceData.TechnicianId || null
            });
            break;
        case 'Part':
            Emitter.emit('open:partForm', actualId);
            break;
        case 'Expense':
            Emitter.emit('open:expenseForm', actualId);
            break;
        case 'Mileage':
            Emitter.emit('open:mileageForm', actualId);
            break;
    }
};

const tagSourceEntities = async (selected: JobItem[]) => {
    const taskIds: number[] = [];
    const partIds: number[] = [];
    const expenseIds: number[] = [];
    const mileageIds: number[] = [];

    for (const item of selected) {
        const id = item.sourceData?.Id;
        if (!id) continue;
        switch (item.type) {
            case 'Task': taskIds.push(id); break;
            case 'Part': partIds.push(id); break;
            case 'Expense': expenseIds.push(id); break;
            case 'Mileage': mileageIds.push(id); break;
        }
    }

    try {
        await Api.tagEntities({
            WorkOrderId: props.workOrderId,
            TaskIds: taskIds.length > 0 ? taskIds : undefined,
            PartIds: partIds.length > 0 ? partIds : undefined,
            ExpenseIds: expenseIds.length > 0 ? expenseIds : undefined,
            MileageIds: mileageIds.length > 0 ? mileageIds : undefined
        });
    } catch (err) {
        console.error('Failed to tag source entities to invoice:', err);
    }
};

const onDismiss = () => {
    emit('close');
};

onMounted(() => {
    loadJobItems();
});
</script>

<style scoped>
.sticky-top {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f8f9fa;
}

.table-hover tbody tr:hover {
    background-color: #f5f5f5;
}

a {
    cursor: pointer;
    color: #0d6efd;
}

a:hover {
    color: #0a58ca;
    text-decoration: underline !important;
}
</style>
